import { z } from 'zod';

const UserView = z.object({
  deleted: z.boolean().optional(),
  id: z.number(),
  /** Filter definition as JSON string */
  mandatory_filters: z.string(),
  name: z.string().optional(),
  schema_version: z.number().optional(),
  /** Anonymization rules */
  anonymization_filter_type: z
    .enum(['ALWAYS_RAW', 'ALWAYS_ANONYMIZED', 'RULE_BASED'])
    .optional(),
  anonymization_filter_rules: z.string().optional().nullable(),
});
export type UserView = z.infer<typeof UserView>;

const Permission = z.object({
  additional_info: z.nullable(z.unknown()),
  deleted: z.boolean(),
  description: z.string().nullable(),
  display_name: z.string(),
  group_type: z.object({
    display_name: z.string(),
    id: z.number(),
    name: z.string(),
  }),
  id: z.number(),
  name: z.string(),
  // TODO: Remove optional with release v51
  directly_assigned: z.boolean().optional(),
  inherited: z.boolean().optional(),
});
export type Permission = z.infer<typeof Permission>;

const ScopeExtended = z.object({
  name: z.string(),
  directly_assigned: z.boolean(),
  inherited: z.boolean(),
});

export type ScopeExtended = z.infer<typeof ScopeExtended>;

const RoleGroup = z.object({
  additional_info: z.nullable(z.unknown()),
  deleted: z.boolean(),
  description: z.string().nullable(),
  display_name: z.string(),
  group_type: z.object({
    display_name: z.string(),
    id: z.number(),
    name: z.string(),
  }),
  id: z.number(),
  name: z.string(),
});

// import { UserRoleData } from '@asset-metrix/portal-api-client-js/schema';
// should be infered from portal-api-client;
// const UserRoles: z.ZodType<UserRoleData>
const UserRoles = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  scopes: z.array(z.string()),
  groups: z.array(RoleGroup),
  user_views: z.array(z.number()),
});
export type UserRoles = z.infer<typeof UserRoles>;

export const User = z.object({
  active: z.boolean(),
  active_user_view: z.nullable(UserView),
  disclaimer_version: z.nullable(z.number()),
  email: z.string(),
  exclude_from_token_validation: z.nullable(z.boolean()),
  first_name: z.nullable(z.string()),
  groups: z.array(Permission),
  id: z.number(),
  idp: z.nullable(z.string()),
  internal: z.boolean(),
  last_name: z.nullable(z.string()),
  salutation: z.nullable(z.string()),
  provider_user_id: z.nullable(z.string()),
  scopes: z.array(z.string()),
  // TODO: Remove optional with release v51
  scopes_extended: z.array(ScopeExtended).optional(),
  self_url: z.nullable(z.string()),
  user_name: z.nullable(z.string()),
  user_views: z.nullable(z.array(z.number())),
  // TODO: Remove optional with release v51
  user_roles: z.array(UserRoles).optional(),
  start_date: z.nullable(z.string()),
  end_date: z.nullable(z.string()),
  /**
   * Set by portal-api when the account is managed externally (for example by
   * the user importer). Superadmin exceptions are already applied server-side.
   */
  readonly: z.boolean().optional(),
});
export type User = z.infer<typeof User>;

export const LoginResponse = z.object({
  expiration: z.number(),
  id: z.number(),
  needsPasswordChange: z.boolean(),
  refresh_token: z.string(),
  tenant: z.string(),
  tenant_name_or_api_id: z.string(),
  token: z.string(),
  user: User,
});
export type LoginResponse = z.infer<typeof LoginResponse>;

export const LoginModeResponse = z.object({
  changePasswordUrl: z.nullable(z.string()).optional(),
  mode: z.union([z.literal('local'), z.literal('multi'), z.literal('sso')]),
  resetPasswordUrl: z.nullable(z.string()).optional(),
});

export type LoginModeResponse = z.infer<typeof LoginModeResponse>;
